import { ChangeDetectionStrategy, Component, inject, output, signal } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { NgTemplateOutlet } from '@angular/common';
import { TsButtonModule, TsDividerComponent, TSIconModule, TSTreeMenuModule, TSTreeNode } from 'tsui';
import { ReportingService } from 'app/modules/reporting/reporting.service';
import { ReportTypeQueryParamValues } from '@modules/reports/reports.types';
import { map, of, tap } from 'rxjs';
import { DashboardService } from '@modules/dashboard/dashboard.service';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { Router, RouterModule } from '@angular/router';
import { Store } from '@ngxs/store';
import { UserState } from '@state/user/state';
import {
  PDF_REPORTS_ACCESS_PERMISSIONS,
  REPORT_BUILDER_ACCESS_PERMISSIONS,
} from 'app/modules/reporting/reporting.constants';

@Component({
  selector: 'app-shell-report-sidebar',
  standalone: true,
  imports: [
    TSIconModule,
    TsButtonModule,
    NgTemplateOutlet,
    MatProgressSpinnerModule,
    TSTreeMenuModule,
    RouterModule,
    TsDividerComponent,
  ],
  templateUrl: './shell-report-sidebar.component.html',
  styleUrl: './shell-report-sidebar.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ShellReportSidebarComponent {
  onNavigate = output<void>();
  isLoading = signal({ reportBuilder: true, pdfReports: true, dashboards: true });

  private readonly reportingService = inject(ReportingService);
  private readonly dashboardService = inject(DashboardService);
  private readonly router = inject(Router);
  private readonly store = inject(Store);

  protected readonly canAccessBuilder = REPORT_BUILDER_ACCESS_PERMISSIONS.some((p) =>
    this.store.selectSnapshot(UserState.hasPermission(p)),
  );
  protected readonly canAccessPdf = PDF_REPORTS_ACCESS_PERMISSIONS.some((p) =>
    this.store.selectSnapshot(UserState.hasPermission(p)),
  );

  readonly reportBuilderReports = toSignal(
    this.canAccessBuilder
      ? this.reportingService
          .getReports({
            page: 1,
            per_page: 10,
            report_types: [ReportTypeQueryParamValues.SingleObject, ReportTypeQueryParamValues.TimeSeries],
          })
          .pipe(
            map((response): TSTreeNode[] =>
              response.results.map((view) => ({
                id: String(view.id),
                name: view.name,
                icon: 'ts_list-ul',
                link: '/reporting/builder/' + view.id,
              })),
            ),
            tap(() => this.isLoading.update((s) => ({ ...s, reportBuilder: false }))),
          )
      : of([]).pipe(tap(() => this.isLoading.update((s) => ({ ...s, reportBuilder: false })))),
    { initialValue: [] as TSTreeNode[] },
  );

  readonly pdfReports = toSignal(
    this.canAccessPdf
      ? this.reportingService
          .getReports({
            page: 1,
            per_page: 10,
            report_types: [ReportTypeQueryParamValues.Legacy],
          })
          .pipe(
            map((response): TSTreeNode[] =>
              response.results.map((view) => ({
                id: String(view.id),
                name: view.name,
                icon: 'ts_list-ul',
                link: '/reports/' + view.id,
              })),
            ),
            tap(() => this.isLoading.update((s) => ({ ...s, pdfReports: false }))),
          )
      : of([]).pipe(tap(() => this.isLoading.update((s) => ({ ...s, pdfReports: false })))),
    { initialValue: [] as TSTreeNode[] },
  );

  readonly dashboards = toSignal(
    this.dashboardService.getDashboards().pipe(
      map((response): TSTreeNode[] =>
        response.map((view) => ({
          id: String(view.id),
          name: view.name,
          icon: 'ts_list-ul',
          link: '/dashboard/' + view.id,
        })),
      ),
      tap(() => this.isLoading.update((s) => ({ ...s, dashboards: false }))),
    ),
    { initialValue: [] as TSTreeNode[] },
  );

  delegateLeafClickFn(route: string): void {
    this.router.navigateByUrl(route);
    this.onNavigate.emit();
  }
}
